import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { apiClient } from '../../api';
import { showToast } from '../../utils/toaster';
import { formatDateTime } from '../../utils/stringUtils';

interface CaptureStepProps {
  protocol: 'a2a' | 'ap2' | 'mcp' | 'mpx';
  onPayloadCaptured: (payload: any, derivedSchema: any) => void;
  onBack: () => void;
}

interface TempChannelInfo {
  configId: string;
  channelName: string;
  endpointUrl: string;
  ttlSeconds: number;
}

const CaptureStep: React.FC<CaptureStepProps> = ({ protocol, onPayloadCaptured, onBack }) => {
  const { state } = useApp();
  const [tempChannel, setTempChannel] = useState<TempChannelInfo | null>(null);
  const [isCreatingChannel, setIsCreatingChannel] = useState(true);
  const [capturedPayloads, setCapturedPayloads] = useState<Array<{
    timestamp: Date;
    payload: any;
    channel: string;
    validation_status: string;
    validation_error?: string;
    derived_schema?: any;
  }>>([]);
  const [selectedCapture, setSelectedCapture] = useState<{
    timestamp: Date;
    payload: any;
    channel: string;
    validation_status: string;
    validation_error?: string;
    derived_schema?: any;
  } | null>(null);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [isCapturing, setIsCapturing] = useState(true);
  const [latestPayload, setLatestPayload] = useState<string>('');
  const [latestDerivedSchema, setLatestDerivedSchema] = useState<string>('');
  const [latestValidationStatus, setLatestValidationStatus] = useState<string>('success');
  const [latestValidationError, setLatestValidationError] = useState<string | undefined>(undefined);
  const [payloadCount, setPayloadCount] = useState(0);
  const [channelExpired, setChannelExpired] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const latestPayloadRef = useRef<HTMLTextAreaElement>(null);

  // Create temporary onboarding channel on mount
  useEffect(() => {
    let channelConfigId: string | null = null;

    const createChannel = async () => {
      try {
        setIsCreatingChannel(true);
        const response = await apiClient.createTempOnboardChannel(protocol);
        channelConfigId = response.config_id;
        setTempChannel({
          configId: response.config_id,
          channelName: response.channel_name,
          endpointUrl: response.endpoint_url,
          ttlSeconds: response.ttl_seconds,
        });
        setTimeRemaining(response.ttl_seconds);
      } catch (error: any) {
        showToast('error', error.message || 'Failed to create onboarding channel');
      } finally {
        setIsCreatingChannel(false);
      }
    };

    createChannel();

    // Cleanup: delete temp channel when component unmounts
    return () => {
      if (channelConfigId) {
        // Try to delete the temp channel
        // Note: This may not complete if browser is closing/refreshing
        // Backend auto-expiration timer will clean up after TTL expires
        apiClient.deleteTempOnboardChannel(channelConfigId)
          .catch(err => console.error('Failed to cleanup temp channel:', err));
      }
    };
  }, [protocol]); // Include protocol in deps

  // Countdown timer effect
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeRemaining]);

  useEffect(() => {
    // Set up WebSocket listener for payload capture events
    const handleWebSocketMessage = (event: Event) => {
      const customEvent = event as CustomEvent;
      const data = customEvent.detail;
        
      // Check if this channel expired
      if (data.type === 'channel_expired' && tempChannel && data.config_id === tempChannel.configId) {
        setChannelExpired(true);
        setTempChannel(null);
        setTimeRemaining(null);
      }
      
      // Only capture payloads for this specific temporary onboarding channel - filter by config_id
      if (data.type === 'payload_captured' && tempChannel && data.config_id === tempChannel.configId) {
        const newPayload = {
          timestamp: new Date(data.timestamp || new Date()),
          payload: data.payload,
          channel: data.channel,
          validation_status: data.validation_status || "success",
          validation_error: data.validation_error,
          derived_schema: data.derived_schema
        };

        setCapturedPayloads(prev => [newPayload, ...prev.slice(0, 9)]); // Keep last 10 payloads
        setLatestPayload(JSON.stringify(data.payload, null, 2));
        setLatestDerivedSchema(JSON.stringify(data.derived_schema, null, 2));
        setLatestValidationStatus(data.validation_status || "Validation OK");
        setLatestValidationError(data.validation_error);
        setPayloadCount(prev => prev + 1);
      }
    };

    // Listen to custom event instead of raw WebSocket message
    window.addEventListener('ws-message', handleWebSocketMessage);

    return () => {
      window.removeEventListener('ws-message', handleWebSocketMessage);
      setIsCapturing(false);
      document.body.classList.remove('modal-open');
    };
  }, [state.wsConnection, tempChannel]);

  const clearPayloads = () => {
    setCapturedPayloads([]);
    setLatestPayload('');
    setLatestDerivedSchema('');
    setLatestValidationStatus('success');
    setLatestValidationError(undefined);
    setPayloadCount(0);
  };

  const openCaptureModal = (capture: typeof capturedPayloads[0]) => {
    setSelectedCapture(capture);
    setShowCaptureModal(true);
    document.body.classList.add('modal-open');
  };

  const closeCaptureModal = () => {
    setShowCaptureModal(false);
    setSelectedCapture(null);
    document.body.classList.remove('modal-open');
  };

  const handleUseLatestPayload = async () => {
    if (capturedPayloads.length > 0) {
      // Delete the temporary channel before proceeding
      if (tempChannel?.configId) {
        try {
          await apiClient.deleteTempOnboardChannel(tempChannel.configId);
          showToast('success', 'Onboarding channel closed');
        } catch (error: any) {
          console.error('Failed to delete temp channel:', error);
          // Continue anyway
        }
      }
      onPayloadCaptured(capturedPayloads[0].payload, capturedPayloads[0].derived_schema);
    }
  };

  const handleRestartCapture = async () => {
    setChannelExpired(false);
    setIsCreatingChannel(true);
    clearPayloads();
    
    try {
      const response = await apiClient.createTempOnboardChannel(protocol);
      setTempChannel({
        configId: response.config_id,
        channelName: response.channel_name,
        endpointUrl: response.endpoint_url,
        ttlSeconds: response.ttl_seconds,
      });
      setTimeRemaining(response.ttl_seconds);
      showToast('success', 'New onboarding channel created');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to create onboarding channel');
    } finally {
      setIsCreatingChannel(false);
    }
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <h5 className="mb-3">
          <i className="fas fa-satellite-dish mr-2"></i>
          Capture Agent Payload
        </h5>
        
        {/* Status Messages */}
        {channelExpired ? (
          <div className="mb-4">
            <div className="border rounded p-3 bg-light">
              <h6 className="mb-2 text-danger">
                <i className="fas fa-hourglass-end mr-2"></i>
                Onboarding Timeout Expired
              </h6>
              <p className="mb-3" style={{ fontSize: '14px' }}>
                The onboarding timeout has expired, and the temporary onboarding channel has been closed.
                <br/>
                Try restarting the capture process again - note that the onboarding channel details will be different!
              </p>
              <button
                className="btn btn-primary"
                onClick={handleRestartCapture}
              >
                <i className="fas fa-redo mr-2"></i>
                Restart Capture
              </button>
            </div>
          </div>
        ) : isCreatingChannel ? (
          <div className="alert alert-info">
            <i className="fas fa-spinner fa-spin mr-2"></i>
            Creating temporary onboarding endpoint...
          </div>
        ) : !tempChannel ? (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            Failed to create onboarding endpoint. Please try again.
          </div>
        ) : null}

        {/* Onboarding Endpoint Section */}
        {!channelExpired && tempChannel && (
          <div className="mb-4">
            <div className="border rounded p-3 bg-light">
              <h6 className="mb-2 text-success">
                <i className="fas fa-check-circle mr-2"></i>
                Onboarding Endpoint Ready
              </h6>
              <p className="mb-2" style={{ fontSize: '14px' }}>
                Point your {protocol.toUpperCase()} agent to this temporary endpoint within the timeout:
              </p>
              <div className="d-flex align-items-stretch mb-2">
                {timeRemaining !== null && timeRemaining > 0 && (
                  <div className="mr-3 d-flex align-items-center" style={{ minWidth: '140px' }}>
                    <span className="badge badge-warning" style={{ fontSize: '0.9rem', padding: '0.4rem 0.6rem' }}>
                      <i className="fas fa-clock mr-1"></i>
                      Expires in: {timeRemaining}s
                    </span>
                  </div>
                )}
                <div className="input-group">
                  <div className="input-group-prepend">
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(tempChannel.endpointUrl);
                        showToast('success', 'Endpoint URL copied to clipboard');
                      }}
                      title="Copy to clipboard"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, height: '38px' }}
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <input
                    title="Onboarding Endpoint URL"
                    type="text"
                    className="form-control font-monospace"
                    value={tempChannel.endpointUrl}
                    readOnly
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, height: '38px' }}
                  />
                </div>
              </div>
              <small className="text-muted">
                This endpoint will only be available for a short time for security reasons, and will automatically close when you proceed to the next step, or the timer expires.
              </small>
            </div>
          </div>
        )}

        {!channelExpired && (
        <>
        
        {/* Latest Payload Display */}
        <div className="mb-3" style={{ display: latestPayload.length === 0 ? 'none' : 'block' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center">
              <h6 className="m-0 font-weight-bold text-success mr-3" style={{ fontSize: '15px' }}>
                <i className="fas fa-file-code"></i> Most Recent Agent Payload
              </h6>
              {latestPayload && (
                <span 
                  className={`badge badge-pill ${latestValidationStatus === 'success' ? 'badge-success' : 'badge-danger'}`}
                >
                  {latestValidationStatus === 'success' ? (
                    <>
                      <i className="fas fa-check-circle"></i> Validation OK
                    </>
                  ) : (
                    <>
                      <i className="fas fa-exclamation-triangle"></i> Failed Validation
                    </>
                  )}
                </span>
              )}
            </div>
            {latestPayload && (
              <button 
                className="btn btn-sm btn-outline-secondary" 
                onClick={clearPayloads}
                title="Clear all payloads"
              >
                <i className="fas fa-trash"></i> Clear
              </button>
            )}
          </div>

          {!latestPayload ? (
            <div className="text-center text-muted py-4 border rounded">
              <i className="fas fa-hourglass-half fa-2x mb-2"></i>
              <p className="mb-1">Waiting for agent requests...</p>
              <small>Make a request to any channel to the temporary channel to capture the payload here.</small>
            </div>
          ) : (
            <div>
              {latestValidationError && (
                <div className="alert alert-danger mb-3">
                  <h6><i className="fas fa-exclamation-circle"></i> Validation Error</h6>
                  <small className="font-monospace">{latestValidationError}</small>
                </div>
              )}
              <div className="row">
                <div className="col-md-6">
                  <h6 className="font-weight-bold mb-2" style={{ fontSize: '14px' }}>
                    <i className="fas fa-file-code mr-2"></i>
                    Captured Payload
                  </h6>
                  <textarea 
                    title='Captured Payload'
                    ref={latestPayloadRef}
                    className={`form-control ${latestValidationStatus === 'success' ? 'is-valid' : 'is-invalid'}`}
                    rows={15}
                    value={latestPayload}
                    readOnly
                    style={{ fontFamily: 'monospace', fontSize: '11px' }}
                  />
                </div>
                <div className="col-md-6">
                  <h6 className="font-weight-bold mb-2" style={{ fontSize: '14px' }}>
                    <i className="fas fa-project-diagram mr-2"></i>
                    Derived Schema
                  </h6>
                  <textarea 
                    title='Derived Schema'
                    className="form-control border-info"
                    rows={15}
                    value={latestDerivedSchema}                    
                    style={{ fontFamily: 'monospace', fontSize: '11px', backgroundColor: '#f8f9fa' }}
                  />
                  <small className="form-text text-muted">
                    This schema was automatically derived from the agentIdentity metadata in the captured payload
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payload History */}
        {capturedPayloads.length > 1 && (
          <div className="mb-3">
            <h6 className="font-weight-bold text-info mb-2" style={{ fontSize: '15px' }}>
              <i className="fas fa-history"></i> Payload History ({capturedPayloads.length}/10) 
              <small className="text-muted ml-2">Click any row to view full details</small>
            </h6>
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Channel</th>
                    <th>Validation Status</th>
                    <th>Payload Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {capturedPayloads.slice(1).map((capture, index) => (
                    <tr 
                      key={index} 
                      onClick={() => openCaptureModal(capture)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ fontSize: '12px' }}>
                        {formatDateTime(capture.timestamp, true)}
                      </td>
                      <td>
                        <span className="badge badge-secondary">{capture.channel}</span>
                      </td>
                      <td>
                        <span className={`badge ${capture.validation_status === 'success' ? 'badge-success' : 'badge-danger'}`}>
                          {capture.validation_status === 'success' ? (
                            <><i className="fas fa-check-circle"></i> OK</>
                          ) : (
                            <><i className="fas fa-exclamation-triangle"></i> Failed</>
                          )}
                        </span>
                      </td>
                      <td>
                        <code style={{ fontSize: '11px' }}>
                          {JSON.stringify(capture.payload).substring(0, 80)}
                          {JSON.stringify(capture.payload).length > 80 && '...'}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}

        {/* Step Navigation */}
        <div className="step-navigation">
          <button 
            className="btn btn-secondary" 
            onClick={onBack}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleUseLatestPayload}
            disabled={!latestPayload || latestValidationStatus !== 'success'}
          >
            Use This Schema
            <i className="fas fa-arrow-right ml-2"></i>
          </button>
        </div>

        {/* Capture Details Modal */}
        {showCaptureModal && selectedCapture && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content modal-content-flex">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-eye"></i> Captured Payload Details
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={closeCaptureModal}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body modal-body-flex">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <strong>Channel:</strong> 
                      <span className="badge badge-secondary ml-2">{selectedCapture.channel}</span>
                    </div>
                    <div className="col-md-8">
                      <strong>Timestamp:</strong> {formatDateTime(selectedCapture.timestamp, true)}
                    </div>
                  </div>
                  
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <strong>Validation Status:</strong> 
                      <span className={`badge ml-2 ${selectedCapture.validation_status === 'success' ? 'badge-success' : 'badge-danger'}`}>
                        {selectedCapture.validation_status === 'success' ? (
                          <><i className="fas fa-check-circle"></i> Validation OK</>
                        ) : (
                          <><i className="fas fa-exclamation-triangle"></i> Failed Validation</>
                        )}
                      </span>
                    </div>
                  </div>

                  {selectedCapture.validation_error && (
                    <div className="mb-3">
                      <label className="form-label font-weight-bold text-danger">
                        <i className="fas fa-exclamation-circle"></i> Validation Error Details
                      </label>
                      <div className="alert alert-danger">
                        <small className="font-monospace">{selectedCapture.validation_error}</small>
                      </div>
                    </div>
                  )}
                  
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <label className="form-label font-weight-bold mb-2">
                      <i className="fas fa-file-code"></i> Full Payload JSON
                    </label>
                    <textarea 
                      title="Full Payload JSON"
                      className="form-control"
                      value={JSON.stringify(selectedCapture.payload, null, 2)}
                      readOnly
                      style={{ fontFamily: 'monospace', fontSize: '13px', flex: 1, minHeight: '400px', resize: 'none' }}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-outline-secondary mr-2"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(JSON.stringify(selectedCapture.payload, null, 2));
                      } catch (error) {
                        console.error('Failed to copy to clipboard:', error);
                      }
                    }}
                  >
                    <i className="fas fa-copy"></i> Copy to Clipboard
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={closeCaptureModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}
      </div>
    </div>
  );
};

export default CaptureStep;
